// Management › Staff productivity — routine slots, tasks completed, open/overdue workload, task activity, 8-week trend.
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import Layout from "../../components/Layout";
import { useAuth } from "../../context/AuthContext";
import "../workspace/workspace.css";
import { isMgmt, workspaceApi } from "../workspace/_shared";
import TaskDrawer from "../workspace/panels/TaskDrawer";
import { Tabs } from "./TaskOverwatch";

const API = process.env.REACT_APP_BACKEND_URL + "/api/management/productivity";
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const tone = (v) => (v === null || v === undefined ? "text-slate-500" : v >= 85 ? "text-emerald-300" : v >= 60 ? "text-amber-300" : "text-rose-300");

function Tile({ label, value, sub, cls = "text-white", testid }) {
  return (
    <div className="ws-card !p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${cls}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function SlotBar({ r }) {
  const s = r.slots || 0;
  if (!s) return <span className="text-slate-600 text-[11px]">no slots</span>;
  const w = (n) => `${(100 * n) / s}%`;
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-2 w-28 rounded-full overflow-hidden bg-white/[0.05]">
        <div className="bg-teal-400" style={{ width: w(r.on_time) }} /><div className="bg-amber-300" style={{ width: w(r.caught_up) }} /><div className="bg-rose-500" style={{ width: w(r.missed) }} />
      </div>
      <span className="text-[11px] font-mono text-slate-300">{r.on_time}/{r.caught_up}/{r.missed}</span>
    </div>
  );
}

function People({ rows, onOpen }) {
  return (
    <div className="ws-card !p-0 overflow-x-auto" data-testid="prod-people">
      <table className="w-full text-[12.5px] min-w-[980px]">
        <thead><tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-white/[0.06]">
          <th className="px-3 py-2">Person</th><th>Routine slots<div className="normal-case tracking-normal text-[9.5px] text-slate-600">on time / caught up / missed</div></th><th>On time</th>
          <th>Tasks done</th><th>Done on time</th><th>Avg days</th><th>Open</th><th>Overdue</th><th>Oldest overdue</th><th className="pr-3">Activity<div className="normal-case tracking-normal text-[9.5px] text-slate-600">comments · updates · ticks</div></th></tr></thead>
        <tbody>{rows.map((p) => (
          <tr key={p.user_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`prod-row-${p.user_id}`}>
            <td className="px-3 py-2"><div className="text-white">{p.name}</div><div className="text-[10.5px] text-slate-500 truncate max-w-[200px]">{p.teams.join(", ")}</div></td>
            <td><SlotBar r={p.routine} /></td>
            <td className={`font-mono ${tone(p.routine.on_time_pct)}`}>{pct(p.routine.on_time_pct)}</td>
            <td className="font-mono text-white">{p.tasks.done}</td>
            <td className={`font-mono ${tone(p.tasks.on_time_pct)}`}>{pct(p.tasks.on_time_pct)}</td>
            <td className="font-mono text-slate-300">{p.tasks.avg_days ?? "—"}</td>
            <td className="font-mono text-slate-300">{p.workload.open}</td>
            <td className={`font-mono ${p.workload.overdue ? "text-rose-300" : "text-slate-500"}`}>{p.workload.overdue}</td>
            <td>{p.workload.oldest ? <button onClick={() => onOpen(p.workload.oldest.id)} className="text-left text-[11.5px] text-rose-200 hover:underline truncate max-w-[200px] block" data-testid={`prod-oldest-${p.user_id}`}>{p.workload.oldest.ref} · {p.workload.oldest.days}d · {p.workload.oldest.title}</button> : <span className="text-slate-600">—</span>}</td>
            <td className="pr-3 font-mono text-slate-300">{p.activity.total}<span className="text-slate-500 text-[10.5px] ml-1">{p.activity.comments}·{p.activity.updates}·{p.activity.ticks}</span></td>
          </tr>))}
          {!rows.length && <tr><td colSpan={10} className="text-center text-slate-500 py-8">No activity for this week.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Teams({ rows }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="prod-teams">
      {rows.map((t) => (
        <div key={t.team_id} className="ws-card !p-3 space-y-2" data-testid={`prod-team-${t.team_id}`}>
          <div className="flex items-center justify-between"><span className="text-white text-[13px] font-medium">{t.name}</span><span className={`font-mono text-[12px] ${tone(t.routine.on_time_pct)}`}>{pct(t.routine.on_time_pct)} on time</span></div>
          <SlotBar r={t.routine} />
          <div className="flex gap-4 text-[11.5px] text-slate-400"><span><b className="text-white">{t.done}</b> done</span><span><b className="text-white">{t.open}</b> open</span><span><b className={t.overdue ? "text-rose-300" : "text-white"}>{t.overdue}</b> overdue</span></div>
        </div>
      ))}
    </div>
  );
}

function Trend({ data }) {
  return (
    <div className="ws-card !p-3 h-72" data-testid="prod-trend">
      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-2">Last 8 weeks</div>
      <ResponsiveContainer width="100%" height="90%">
        <ComposedChart data={data} margin={{ left: -10, right: 10 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis yAxisId="n" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis yAxisId="p" orientation="right" domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} unit="%" />
          <Tooltip contentStyle={{ background: "#0b1522", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="n" dataKey="done" name="Tasks done" fill="#38bdf8" radius={[3, 3, 0, 0]} />
          <Bar yAxisId="n" dataKey="missed" name="Missed slots" fill="#f43f5e" radius={[3, 3, 0, 0]} />
          <Line yAxisId="p" dataKey="routine_pct" name="Routine on time %" stroke="#2dd4bf" strokeWidth={2} dot={false} connectNulls />
          <Line yAxisId="p" dataKey="done_on_time_pct" name="Tasks on time %" stroke="#fbbf24" strokeWidth={2} dot={false} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function StaffProductivity() {
  const { user } = useAuth();
  const [week, setWeek] = useState(0);
  const [team, setTeam] = useState("");
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [dir, setDir] = useState({ users: [], teams: [] });
  useEffect(() => { Promise.all([workspaceApi.listUsers(), workspaceApi.listTeams("all")]).then(([u, t]) => setDir({ users: u || [], teams: t.items || [] })).catch(() => {}); }, []);
  useEffect(() => {
    setD(null);
    axios.get(API, { params: { week, team_id: team || undefined }, withCredentials: true }).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [week, team]);
  if (user && !isMgmt(user)) return <Navigate to="/" replace />;
  const s = d?.summary || {};
  return (
    <Layout>
      <h1 className="text-xl font-bold text-white mb-3">Management</h1>
      <Tabs />
      <div className="space-y-4" data-testid="mgmt-productivity">
        <div className="flex flex-wrap items-center gap-2">
          <select value={week} onChange={(e) => setWeek(Number(e.target.value))} className="ws-input max-w-[200px]" data-testid="prod-week">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((w) => <option key={w} value={w}>{w === 0 ? "This week" : w === 1 ? "Last week" : `${w} weeks ago`}</option>)}
          </select>
          <select value={team} onChange={(e) => setTeam(e.target.value)} className="ws-input max-w-[220px]" data-testid="prod-team">
            <option value="">All teams</option>
            {(d?.teams_list || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {d?.week && <span className="text-[12px] text-slate-400">{d.week.from} → {d.week.to}</span>}
          {d?.quiet_routines > 0 && <span className="text-[11px] text-slate-500 ml-auto">{d.quiet_routines} quiet routine{d.quiet_routines === 1 ? "" : "s"} not counted</span>}
        </div>
        {!d ? <div className="text-slate-500 py-10 text-center">Loading…</div> : d.error ? <div className="text-rose-300 py-10 text-center">Couldn't load the report.</div> : <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            <Tile label="Routine on time" value={pct(s.routine_on_time_pct)} cls={tone(s.routine_on_time_pct)} sub={`${s.slots || 0} slots${s.unattributed_slots ? ` · ${s.unattributed_slots} on shared/unowned routines` : ""}`} testid="prod-sum-routine" />
            <Tile label="Caught up" value={s.caught_up} cls="text-amber-300" testid="prod-sum-caught" />
            <Tile label="Missed slots" value={s.missed} cls={s.missed ? "text-rose-300" : "text-white"} testid="prod-sum-missed" />
            <Tile label="Tasks done" value={s.done} sub={`${pct(s.done_on_time_pct)} on time`} testid="prod-sum-done" />
            <Tile label="Open" value={s.open} testid="prod-sum-open" />
            <Tile label="Overdue" value={s.overdue} cls={s.overdue ? "text-rose-300" : "text-white"} testid="prod-sum-overdue" />
            <Tile label="Activity" value={s.activity} sub="comments · updates · ticks" testid="prod-sum-activity" />
          </div>
          <Trend data={d.trend} />
          <Teams rows={d.teams} />
          <People rows={d.people} onOpen={setOpen} />
          <p className="text-[11px] text-slate-500">Routine ticks credit the person who ticked; missed slots count against the owner, or the team when the routine is shared. Quiet and paused routines are left out.</p>
        </>}
      </div>
      {open && <TaskDrawer open taskId={open} users={dir.users} teams={dir.teams} onClose={() => setOpen(null)} />}
    </Layout>
  );
}
